/**
 * Emission breakdown client for proba-ui
 * Fetches emission breakdown data via platform's external API endpoint
 */

const getPlatformUrl = (): string => {
  return process.env.REACT_APP_PLATFORM_URL || "http://localhost:3333"
}

const getJwtToken = (): string | null => {
  const jwt = localStorage.getItem("jwt")
  if (!jwt) return null
  // Remove "Bearer " prefix if present
  return jwt.replace(/^Bearer\s+/, "")
}

export interface EmissionSection {
  total: number
  pcf: number
  direct: number
  leaching: number
  volatilization: number
}

export interface StageBreakdown {
  baseline: EmissionSection | null
  project: EmissionSection | null
  reduction: { absolute: number; percentage: number } | null
  totalAsset: { baseline: number; project: number; reduction: number } | null
}

export interface EmissionBreakdownResponse {
  projectId: string
  assetId: string
  period: string
  estimated: StageBreakdown
  realized: StageBreakdown | null
}

/**
 * Fetch emission breakdown for a specific asset and period
 * @param projectId - The project ID
 * @param assetId - The asset ID
 * @param period - The period string (e.g., "2024-1")
 * @returns Promise that resolves to the emission breakdown data or null on error
 */
export async function fetchEmissionBreakdown(
  projectId: string,
  assetId: string,
  period: string
): Promise<EmissionBreakdownResponse | null> {
  try {
    const token = getJwtToken()
    if (!token) {
      console.warn("[Emission Breakdown] No JWT token found in localStorage")
      return null
    }

    const platformUrl = getPlatformUrl()
    const url = new URL(
      "/api/intervention-manager/calculation-results/external/breakdown",
      platformUrl
    )
    url.searchParams.set("projectId", projectId)
    url.searchParams.set("assetId", assetId)
    url.searchParams.set("period", period)

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error(
        `[Emission Breakdown] API error for asset '${assetId}', period '${period}':`,
        response.status,
        errorData
      )
      return null
    }

    const data = await response.json()
    return data as EmissionBreakdownResponse
  } catch (error) {
    console.error(
      `[Emission Breakdown] Error fetching breakdown for asset '${assetId}', period '${period}':`,
      error
    )
    return null
  }
}
